/**
 * Money arriving, and which box it ticks.
 *
 * The production checklist carries four boxes about money: "First Check
 * Submitted" / "First Check", and "Final Check Submitted" / "Final Check". The
 * pair is deliberate — submitted is the payment being taken, the plain one is
 * the money actually being in the bank — and until now a person ticked all four
 * by hand.
 *
 * Jonathon, 2026-08-31: a payment taken in QuickBooks should tick Submitted by
 * itself, and the money landing in Mercury should tick the other one. This is
 * the rule that decides **which pair** — First or Final — from the amount.
 *
 *     the whole contract   → Final (and First too, see below)
 *     the first payment    → First, if it has not already been collected
 *     what is left after First → Final
 *     anything else        → nothing, and a person is told why
 *
 * **Paying in full ticks First as well.** Jonathon asked for the Final boxes,
 * and Final is certainly right; the extra tick exists because the ongoing board
 * holds a job in "Needs First Payment" until First Check is ticked (see
 * docs/BUSINESS-RULES.md). A customer who pays everything at once would strand
 * their job in that column forever, waiting for a payment that is never coming
 * because it already came. The money for the first payment demonstrably
 * arrived, so the box is honest.
 *
 * Under the $1,000 split threshold there is no first payment at all — one line,
 * due on completion — so a full payment ticks Final and nothing else.
 *
 * **Nothing here guesses.** An amount that is not one of the three is not
 * approximately anything: an insurance ACV cheque, a partial, a deposit net of
 * a processing fee are all real, all common, and all a person's call. This
 * returns the reason instead, and the screen says it.
 */

import { PAYMENT_SLOT_LABEL } from "./constants.js";
import {
  PAYMENT_SPLIT_THRESHOLD,
  defaultFirstPayment,
  finalPayment,
} from "./paymentSchedule.js";
import { cardFee } from "./mercury.js";
import { type Cents, cents, formatMoney } from "./money.js";

/**
 * The slots a payment can arrive against — all four of them.
 *
 * It was `1 | 4` until 2026-09-08, on the reasoning that the middle slots have
 * no checklist boxes and so nothing to tick. That was true and it was the
 * wrong conclusion: a rep who types a second and a third payment has sold a
 * job that is paid in four goes, and when the second one lands, a person still
 * has to be able to say that is what it is. Jonathon, looking at the split
 * screen: "if there are amounts on the 2nd or 3rd payment then those need to
 * be options too."
 *
 * A middle payment is recorded against the job and ticks nothing, because
 * there is nothing to tick. That is a smaller thing than being unable to say
 * whose money it was.
 */
export type PaymentSlot = 1 | 2 | 3 | 4;

/**
 * The boxes a payment owns, for the two payments that have any.
 *
 * Deliberately partial. The production checklist carries four money boxes and
 * they are about the two ends of a job — the deposit that starts it and the
 * balance that closes it. The middle payments are draws against work, and the
 * board does not hold a job open waiting for one, so there is no box for them
 * and inventing one would change what the board means.
 */
export const PAYMENT_CHECKLIST_LABELS: Partial<
  Record<PaymentSlot, { submitted: string; cleared: string }>
> = {
  1: { submitted: "First Check Submitted", cleared: "First Check" },
  4: { submitted: "Final Check Submitted", cleared: "Final Check" },
};

/** Whether a payment has boxes at all. False for the middle two. */
export function slotHasCheckboxes(slot: PaymentSlot): boolean {
  return PAYMENT_CHECKLIST_LABELS[slot] != null;
}

/** Every checklist label this module may ever tick. */
export const PAYMENT_CHECKLIST_ITEMS: readonly string[] = [
  PAYMENT_CHECKLIST_LABELS[1]!.submitted,
  PAYMENT_CHECKLIST_LABELS[1]!.cleared,
  PAYMENT_CHECKLIST_LABELS[4]!.submitted,
  PAYMENT_CHECKLIST_LABELS[4]!.cleared,
];

export interface PaymentContext {
  /** The contract, as `quickbooks_invoices.amount_cents` holds it. */
  contractCents: Cents;
  /**
   * The first payment on the schedule, when the rep typed one. Null takes the
   * default — half, rounded to the cent — which is what the schedule itself
   * shows in that case.
   */
  firstCents?: Cents | null;
  /**
   * The middle payments, when the rep typed any — slot 2 and slot 3.
   *
   * Two things depend on them and both were wrong without them. The final
   * payment is what is left after **all three** typed slots, not after the
   * first, so on a job sold in four goes the figure Money In matched a final
   * payment against was too big and no final payment ever matched. And a
   * middle payment landing on its own could not be named at all.
   */
  middleCents?: Readonly<Record<number, Cents | null>> | null;
  /**
   * The contract as the customer signed it, before anything signed since.
   *
   * Only the default first payment reads it, and only to stop that default
   * moving: the deposit is half of what they put their name to, and a change
   * order signed afterwards lands wholly on the Final (ADR 0205). Null on a
   * job with no signature, where there is nothing frozen to hold it to.
   */
  signedContractCents?: Cents | null;
  /** Whether "First Check" is already ticked on this job. */
  firstCollected: boolean;
  /**
   * The 3% added because the customer paid that slot on a credit card, keyed
   * by slot.
   *
   * Without this a card payment arrives for *more* than the slot was worth, the
   * shortfall is negative, and `processingFeeExpense` records nothing — so
   * Stripe's cut would silently stop reaching the job's other expenses on
   * exactly the payments that have one. The surcharge and the fee are separate
   * numbers on purpose: the customer pays 3%, Stripe takes 2.9% + 30c, and the
   * job keeps the difference either way.
   */
  surchargeBySlot?: Readonly<Record<number, Cents>> | null;
}

export type PaymentMatch =
  | { matched: true; slots: PaymentSlot[]; why: string }
  | { matched: false; slots: []; why: string };

/**
 * What this job expects each payment to be.
 *
 * Exported because the screens show it — "expecting $9,000 first, $9,000
 * final" beside a deposit that is neither is most of the explanation somebody
 * needs.
 */
export function expectedPayments(context: PaymentContext): {
  first: Cents;
  second: Cents;
  third: Cents;
  final: Cents;
  split: boolean;
} {
  // Already cents on the way in — `toCents` would multiply by a hundred
  // (CLAUDE.md rule 3: money is integer cents, and these are).
  const contract = cents(context.contractCents);
  const split = contract >= PAYMENT_SPLIT_THRESHOLD;
  const first =
    context.firstCents == null
      ? defaultFirstPayment(contract, context.signedContractCents)
      : cents(context.firstCents);
  const middle = (slot: number): Cents =>
    cents(Number(context.middleCents?.[slot] ?? 0));
  const second = middle(2);
  const third = middle(3);

  if (!split) {
    // Under the threshold there is one line, due on completion. A middle
    // amount cannot exist on such a job — `paymentSchedule` errors rather than
    // saving one — so nothing here has to make room for it.
    return {
      first: cents(0),
      second: cents(0),
      third: cents(0),
      final: contract,
      split,
    };
  }

  return {
    first,
    second,
    third,
    // `finalPayment`, the same subtraction the estimate builder and the
    // customer's own schedule use, rather than a second one here: the final
    // payment is whatever the first three leave behind, and two subtractions
    // that must agree is one too many.
    final: finalPayment(contract, first, second, third),
    split,
  };
}

/**
 * The payments this job actually has, for a screen that must offer them.
 *
 * Only slots carrying money. A job sold 50/50 offers two; a job sold in four
 * draws offers four; and the middle two are simply absent from the list on
 * every job where nobody typed them, which is nearly all of them.
 *
 * The label is `PAYMENT_SLOT_LABEL`, the same four words the estimate builder,
 * the proposal and the customer's own schedule use. A person matching money
 * should be reading the words they sold the job in.
 */
export function paymentsOnOffer(
  context: PaymentContext,
): { slot: PaymentSlot; label: string; amount: Cents }[] {
  const { first, second, third, final } = expectedPayments(context);
  const surcharge = (slot: number): Cents =>
    cents(Number(context.surchargeBySlot?.[slot] ?? 0));
  return (
    [
      [1, first],
      [2, second],
      [3, third],
      [4, final],
    ] as [PaymentSlot, Cents][]
  )
    .filter(([, amount]) => amount > 0)
    .map(([slot, amount]) => ({
      slot,
      label: PAYMENT_SLOT_LABEL[slot] ?? `Payment ${slot}`,
      // What we expect to arrive, card fee included — the figure somebody is
      // about to compare a bank line against.
      amount: cents(amount + surcharge(slot)),
    }));
}

/**
 * Which boxes an amount of money ticks on this job.
 *
 * Exact cents. A near miss is not a match — see the note at the top of the
 * file about why approximately is not good enough for a box that moves a job's
 * stage to complete.
 */
/**
 * The most a processor could plausibly have taken out of a payment.
 *
 * Jonathon, 2026-08-31: "received money will often be slightly different than
 * the estimate total due to the fees." Before this, `matchPayment` compared
 * exactly — so a $9,000 payment landing as $8,738.70 matched nothing, and every
 * card and ACH deposit went to a person to sort out by hand.
 *
 * The band is the card fee, because a card is the dearest way money reaches
 * this company: 2.9% + 30c (`cardFee`). ACH through Mercury is free and
 * QuickBooks' is cheaper, so both sit well inside it.
 *
 * Deliberately an upper bound and not a guess at the actual fee. What the fee
 * actually was is the difference between what was expected and what arrived —
 * `processingFee` below — and that is what gets recorded. This only decides
 * whether a shortfall is small enough to be a fee at all.
 */
export function feeTolerance(expected: Cents): Cents {
  return cardFee(expected);
}

/**
 * What a processor kept, given what was expected and what arrived.
 *
 * Zero when the money arrived whole or over — an overpayment is not a fee, and
 * treating it as one would book a negative cost against the job.
 */
export function processingFee(expected: Cents, received: Cents): Cents {
  const short = cents(expected) - cents(received);
  return (short > 0 ? short : 0) as Cents;
}

/**
 * Whether an amount that arrived is an expected amount, net of the fee.
 *
 * Short by no more than the fee band is the same payment. Short by more is not
 * — that is a part payment, or somebody else's money, and it goes to a person.
 * Over is never a fee.
 */
function isNetOf(expected: Cents, received: Cents): boolean {
  if (expected <= 0) return false;
  if (received > expected) return false;
  return expected - received <= feeTolerance(expected);
}

/**
 * How the amount being matched relates to the amount that was owed.
 *
 * A Mercury deposit is what reached the bank *after* the processor took its
 * cut, so it is matched net of the fee band. A QuickBooks payment is what the
 * customer paid, gross — QuickBooks takes its fee out of the payout, not the
 * payment — so it is matched to the cent, exactly as before: a customer who
 * paid $8,900 on a $9,000 invoice has short-paid, and that is a person's call,
 * not a fee. Defaulting to exact means a new caller that says nothing gets the
 * stricter rule.
 */
export interface MatchOptions {
  /** What arrived is what the bank kept, after the processor's fee came out. */
  netOfFees?: boolean;
}

function isSame(
  expected: Cents,
  received: Cents,
  options: MatchOptions,
): boolean {
  return options.netOfFees
    ? isNetOf(expected, received)
    : received === expected;
}

/**
 * What the job was owed, gross, for the slots a payment ticked.
 *
 * The other half of working out a fee: the fee is this minus what actually
 * reached the bank. Reads the same schedule `matchPayment` matched against, so
 * the two cannot disagree about what was expected.
 *
 * Both slots ticked at once means the whole contract arrived in one payment,
 * which is a single transaction and a single fee — not the first and the final
 * added together, which would be the same number by a different route on a
 * 50/50 job and a wrong one on any other.
 */
export function expectedForSlots(
  slots: readonly PaymentSlot[],
  context: PaymentContext,
): Cents {
  const contract = cents(context.contractCents);
  const { first, second, third, final } = expectedPayments(context);
  const fee = (slot: number): Cents =>
    cents(Number(context.surchargeBySlot?.[slot] ?? 0));
  if (slots.length === 0) return 0 as Cents;
  if (slots.includes(1) && slots.includes(4)) {
    return cents(contract + fee(1) + fee(4));
  }
  if (slots.includes(1)) return cents(first + fee(1));
  if (slots.includes(4)) return cents(final + fee(4));
  // The middle draws, which only ever arrive one at a time — nothing raises
  // two of them together, and a person choosing one on the split screen
  // chooses exactly one.
  if (slots.includes(2)) return cents(second + fee(2));
  if (slots.includes(3)) return cents(third + fee(3));
  return 0 as Cents;
}

/**
 * What a payment cost to receive, and what to call it on the job.
 *
 * Jonathon, 2026-09-01: every card, debit, ACH and bank-transfer fee "needs to
 * go in other expenses so we have an accurate job cost and margin". A $9,000
 * card payment costs $261.30 to take, and that is a cost of doing the job.
 */
export function processingFeeExpense(input: {
  expected: Cents;
  received: Cents;
  /** What the bank line said, for naming the expense somebody will read. */
  description?: string | null;
}): { amount: Cents; name: string } | null {
  const amount = processingFee(input.expected, input.received);
  if (amount <= 0) return null;
  const said = (input.description ?? "").toLowerCase();
  const how =
    said.includes("ach") || said.includes("transfer")
      ? "Bank transfer fee"
      : "Card processing fee";
  return { amount, name: how };
}

/**
 * The middle draw an amount settles, when it is one.
 *
 * Kept apart from `matchPayment` on purpose. `matchPayment` answers "which box
 * does this tick", and the middle draws tick none — folding them in would turn
 * a card green and say "Matches a payment" while nothing at all happened,
 * which is a worse screen than one that says a person is needed.
 *
 * This answers the different question the screen also has to ask: **is this
 * one of the draws this customer was asked for?** If it is, the button can say
 * so by name — "Record as the second payment" — instead of offering the
 * shrugging "Record it anyway" that was the only thing on offer before.
 *
 * Null when the job has no draws, or the amount is not one of them. The first
 * and the final are never returned: those have boxes and are `matchPayment`'s.
 */
export function middlePaymentFit(
  receivedCents: Cents,
  context: PaymentContext,
  options: MatchOptions = {},
): { slot: PaymentSlot; label: string } | null {
  const received = cents(receivedCents);
  if (received <= 0) return null;
  for (const payment of paymentsOnOffer(context)) {
    if (slotHasCheckboxes(payment.slot)) continue;
    if (isSame(payment.amount, received, options)) {
      return { slot: payment.slot, label: payment.label };
    }
  }
  return null;
}

export function matchPayment(
  receivedCents: Cents,
  context: PaymentContext,
  options: MatchOptions = {},
): PaymentMatch {
  const received = cents(receivedCents);
  const schedule = cents(context.contractCents);
  const {
    first: firstOnSchedule,
    final: finalOnSchedule,
    split,
  } = expectedPayments(context);

  // What we expect to *arrive*, which is the schedule plus any card fee the
  // customer agreed to on that slot. The schedule itself is untouched: a 3%
  // surcharge is not part of the contract and must not change what the final
  // payment is worked out from. Slots with no card behind them add nothing, so
  // every job that has never taken a card matches exactly as it did before.
  const fee = (slot: number): Cents =>
    cents(Number(context.surchargeBySlot?.[slot] ?? 0));
  const first = cents(firstOnSchedule + fee(1));
  const final = cents(finalOnSchedule + fee(4));
  const contract = cents(schedule + fee(1) + fee(4));

  if (received <= 0) {
    return {
      matched: false,
      slots: [],
      why: "Money going out, not coming in.",
    };
  }

  if (schedule <= 0) {
    return {
      matched: false,
      slots: [],
      why: "This job has no contract amount yet, so there is nothing to match against.",
    };
  }

  if (isSame(contract, received, options)) {
    // Paid in full. Final always; First as well when the schedule has one and
    // nobody has ticked it, or the board holds the job in Needs First Payment
    // waiting for money that has already arrived.
    const slots: PaymentSlot[] =
      split && !context.firstCollected ? [1, 4] : [4];
    return {
      matched: true,
      slots,
      why:
        slots.length === 2
          ? `${formatMoney(received)} is the whole contract — first and final.`
          : `${formatMoney(received)} is the whole contract.`,
    };
  }

  // Whether the first has been collected is asked BEFORE which amount this is,
  // because on the default 50/50 schedule the first and the final are the same
  // number. Testing the amount first would read every second $9,000 as another
  // first payment and refuse it, on a job whose final payment had just arrived.
  if (split && !context.firstCollected) {
    if (isSame(first, received, options)) {
      return {
        matched: true,
        slots: [1],
        why: `${formatMoney(received)} is the first payment.`,
      };
    }
    if (isSame(final, received, options)) {
      return {
        matched: false,
        slots: [],
        why: `${formatMoney(received)} is what is left after the first payment, but the first payment has not been collected yet.`,
      };
    }
  }

  if (split && context.firstCollected) {
    if (isSame(final, received, options)) {
      return {
        matched: true,
        slots: [4],
        why: `${formatMoney(received)} is the final payment.`,
      };
    }
    if (isSame(first, received, options)) {
      return {
        matched: false,
        slots: [],
        why: `${formatMoney(received)} is the first payment, but the first payment is already collected on this job.`,
      };
    }
  }

  // Named from the schedule this job actually has, so a job sold in four draws
  // is not told it is not one of two things it never had. The middle draws are
  // in the sentence because they are real money this customer owes, even
  // though matching one is a person's call — there is no box for it, so there
  // is nothing here that could be ticked by getting it right.
  const offered = paymentsOnOffer(context)
    .map(
      (payment) =>
        `${payment.label.toLowerCase()} (${formatMoney(payment.amount)})`,
    )
    .join(", ");
  return {
    matched: false,
    slots: [],
    why: split
      ? `${formatMoney(received)} is not the ${offered} or the whole contract (${formatMoney(contract)}).`
      : `${formatMoney(received)} is not the contract amount (${formatMoney(contract)}).`,
  };
}

/**
 * The checklist labels a match ticks, at the stage it has reached.
 *
 * `submitted` is a payment taken — recorded in QuickBooks. `cleared` is money
 * in the bank — seen in Mercury and confirmed by a person.
 */
export function labelsFor(
  match: PaymentMatch,
  stage: "submitted" | "cleared",
): string[] {
  // The middle draws have no boxes, so they contribute none. They are still
  // recorded against the job and still carry their own processing fee — the
  // ticking is the only part they take no share of.
  return match.slots.flatMap((slot) => {
    const labels = PAYMENT_CHECKLIST_LABELS[slot];
    return labels ? [labels[stage]] : [];
  });
}

/**
 * How close an amount is to something this job is waiting for, for ranking a
 * bank deposit against the jobs it might belong to. Lower is closer; null means
 * the job is not waiting for anything this could be.
 *
 * This ranks a suggestion. It never ticks anything — a person confirms a
 * deposit (Jonathon, 2026-08-31), because a bank line naming "QUICKBOOKS
 * PAYMENTS DEPOSIT" and an amount is not proof of whose money it is.
 */
export function suggestionDistance(
  receivedCents: Cents,
  context: PaymentContext,
): number | null {
  const received = cents(receivedCents);
  const { first, second, third, final, split } = expectedPayments(context);
  const candidates = [cents(context.contractCents), final];
  if (split && !context.firstCollected) candidates.push(first);
  // A draw against work in progress is money this job is genuinely waiting
  // for, so a deposit the size of one should rank this job near the top even
  // though no box will be ticked for it.
  if (split) candidates.push(second, third);

  const distances = candidates
    .filter((amount) => amount > 0)
    .map((amount) => Math.abs(received - amount));
  if (distances.length === 0) return null;
  return Math.min(...distances);
}
